import React, { useEffect, useState } from 'react';
import { useNdr } from '../hooks/useNdr';
import { Loader } from './ui/Loader';
import { Clock, MessageSquare, User, Calendar } from 'lucide-react';
import { formatDateTime } from '../utils/format';
import { cn } from '../utils/cn';

const STATUS_MAP: Record<number, string> = {
  1: 'NDR REPORTED',
  2: 'CLOSED',
  3: 'ACTION TAKEN',
};

interface NdrHistoryProps {
  trackingNo: string;
}

export const NdrHistory: React.FC<NdrHistoryProps> = ({ trackingNo }) => {
  const { getNdrHistory, loading, error } = useNdr();
  const [history, setHistory] = useState<any[]>([]);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await getNdrHistory(trackingNo);
        setHistory(res || []);
      } catch (err: any) {
        // Error is handled by useNdr
      }
    };
    fetchHistory();
  }, [trackingNo, getNdrHistory]);

  if (loading)
    return (
      <div className="py-8 flex justify-center">
        <Loader />
      </div>
    );
  if (error)
    return <div className="py-4 text-center text-red-500 text-sm">{error}</div>;
  if (history.length === 0)
    return (
      <div className="py-4 text-center text-gray-400 text-sm italic">
        No history available for this shipment.
      </div>
    );

  return (
    <div className="bg-gray-50/50 rounded-xl p-4 border border-gray-100 animate-in slide-in-from-top-2 duration-300">
      <div className="space-y-2">
        {history.map((item, idx) => (
          <div
            key={idx}
            className="flex items-center gap-4 py-2 px-3 bg-white rounded-lg border border-gray-50 shadow-sm hover:shadow-md transition-all group"
          >
            {/* Date */}
            <div className="text-sm font-medium text-text2 whitespace-nowrap">
              {formatDateTime(item.ndr_reported_at || item.created_at).date}{' '}
              <span className="text-text3 ml-1">
                {formatDateTime(item.ndr_reported_at || item.created_at).time}
              </span>
            </div>

            {/* Attempt Count */}
            {item.delivery_attempt_count !== undefined && (
              <div className="flex-shrink-0 border-l pl-4 border-gray-100">
                <span className="text-sm bg-orange-50 text-orange-600 px-2 py-0.5 rounded-md font-bold border border-orange-100 whitespace-nowrap">
                  Attempt #{item.delivery_attempt_count}
                </span>
              </div>
            )}

            {/* Remark */}
            <div className="flex-1 min-w-0 border-l pl-4 border-gray-100">
              <p className="text-sm text-gray-600 truncate group-hover:text-gray-900 group-hover:overflow-visible group-hover:whitespace-normal transition-all">
                {item.courier_remark ||
                  item.remarks ||
                  item.additional_comment ||
                  'No remarks'}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
